import { useState } from 'react';
import './VerifyOTP.css';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Mail, Smartphone } from 'lucide-react';
import LogoImage from '../../../assets/Admin/AdminNavbar/logo-image.png';
import Layout from '../../../components/Layout';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import Cookies from 'js-cookie';

const VerifyOTP = () => {
  const [auth, setAuth] = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const SERVER_API_URL = process.env.REACT_APP_SERVER_API_URL;

  // Get email/phone from location state
  const contactInfo = location.state?.email || '';
  const [userContact, setUserContact] = useState(contactInfo);
  const [isEditing, setIsEditing] = useState(false);

  // State for OTP input
  const [otp, setOtp] = useState(['', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);

  // Determine if it's an email or phone number
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isEmail = emailRegex.test(userContact);

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    if (value.length > 1) return; // Only allow single digit

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  // Handle key press for backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // Handle OTP verification
  const handleVerifyOtp = async () => {
    const otpValue = otp.join('');

    if (otpValue.length !== 4) {
      toast.dismiss();
      toast.error('Please enter the complete 4-digit OTP');
      return;
    }

    setIsLoading(true);

    try {
      const payload = isEmail
        ? { email: userContact.toLowerCase(), otp: otpValue }
        : { phone_no: userContact.replace(/\D/g, ''), otp: otpValue };

      const response = await axios.post(`${SERVER_API_URL}/auth/otp-login/validate`, payload, {
        withCredentials: true,
      });

      if (response.status === 200 && response.data) {
        toast.dismiss();
        toast.success('OTP verified successfully!');

        const { id, user_type, access_token, first_name, last_name, email, profile_image } =
          response.data;

        setAuth({
          ...auth,
          id,
          user_type,
          access_token,
          first_name,
          last_name,
          email,
          profile_image,
        });

        // Set cookies
        Cookies.set('access_token', access_token);
        Cookies.set('user_type', user_type);
        Cookies.set('first_name', first_name);
        Cookies.set('last_name', last_name);
        Cookies.set('email', email);
        Cookies.set('profile_image', profile_image);

        // Check user type and navigate accordingly
        if (user_type === 'admin') {
          try {
            const adminResponse = await axios.get(`${SERVER_API_URL}/admin/rbac/admin/me`, {
              headers: {
                Authorization: `Bearer ${access_token}`,
              },
              withCredentials: true,
            });

            const adminData = adminResponse.data;
            const roles = adminData.roles || [];

            let redirectPath = '/admin/dashboard';

            if (roles.includes('operations_officer') || roles.includes('organisation_manager')) {
              redirectPath = '/admin/organisation';
            } else if (
              roles.includes('talent_manager') ||
              roles.includes('user_onboarding') ||
              roles.includes('support_associate')
            ) {
              redirectPath = '/admin/users/instructor';
            } else if (
              roles.includes('finance_officer') ||
              roles.includes('admin') ||
              roles.includes('super_admin')
            ) {
              redirectPath = '/admin/dashboard';
            }

            toast.dismiss();
            toast.success('Login successful');
            navigate(redirectPath);
          } catch (adminError) {
            console.error('Error fetching admin details:', adminError);
            toast.dismiss();
            toast.success('Login successful');
            navigate('/admin/dashboard');
          }
        } else {
          toast.dismiss();
          toast.error('Not Authorized');
        }
      } else {
        toast.dismiss();
        toast.error(response?.data?.detail || 'Verification failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit contact info
  const handleEdit = () => {
    setIsEditing(true);
  };

  // Handle save edited contact info
  const handleSaveContact = () => {
    if (!userContact) {
      toast.dismiss();
      toast.error('Please enter your email or phone number');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmail = emailRegex.test(userContact);

    if (isEmail) {
      if (!emailRegex.test(userContact)) {
        toast.dismiss();
        toast.error('Please enter a valid email address');
        return;
      }
    } else {
      // Validate phone number
      const phoneDigits = userContact.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        toast.dismiss();
        toast.error('Please enter a valid phone number');
        return;
      }
    }

    setIsEditing(false);
    // Resend OTP to new contact
    handleResendOtp();
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    try {
      const payload = isEmail
        ? { email: userContact.toLowerCase() }
        : { phone_no: userContact.replace(/\D/g, '') };

      const response = await axios.post(`${SERVER_API_URL}/auth/otp-login`, payload, {
        withCredentials: true,
      });

      if (response.status === 200 && response.data) {
        toast.dismiss();
        toast.success('OTP resent successfully!');
        setOtp(['', '', '', '']);
      } else {
        toast.dismiss();
        toast.error(response?.data?.detail);
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail);
    }
  };

  const handleBackArrowClick = () => {
    navigate('/');
  };

  return (
    <Layout title="Klass Online | Verify OTP">
      <Box className="verify-otp-container">
        {/* Header Section */}
        <Box className="verify-otp-header">
          <Box className="back-button-container">
            <button onClick={handleBackArrowClick} className="back-button">
              <ArrowLeft className="back-icon" />
            </button>
          </Box>

          <Box className="verify-otp-logo">
            <img src={LogoImage} alt="Klass Online Logo" className="logo-image" />
          </Box>

          <Typography variant="h3" className="verify-otp-title">
            Verify {isEmail ? 'Email' : 'Phone Number'}
          </Typography>
          <Typography variant="body1" className="verify-otp-subtitle">
            Enter the code we've sent to{' '}
            <span className="contact-display">{isEmail ? userContact : `+${userContact}`}</span>
            <span className="edit-link" onClick={handleEdit}>
              Edit
            </span>
          </Typography>
        </Box>

        {/* Main Card */}
        <Box className="verify-otp-card">
          {isEditing ? (
            <form
              className="verify-otp-form"
              onSubmit={e => {
                e.preventDefault();
                handleSaveContact();
              }}
            >
              <Box className="otp-input-group">
                <label htmlFor="contact" className="otp-input-label">
                  {isEmail ? 'Email' : 'Phone Number'}
                </label>
                <Box className="otp-input-wrapper">
                  {isEmail ? (
                    <Mail className="otp-input-icon" />
                  ) : (
                    <Smartphone className="otp-input-icon" />
                  )}
                  <input
                    id="contact"
                    className="otp-input-field"
                    type="text"
                    placeholder={isEmail ? 'Enter your email address' : 'Enter your phone number'}
                    value={userContact}
                    onChange={e => setUserContact(e.target.value)}
                    required
                  />
                </Box>
              </Box>

              <button type="submit" className="primary-button">
                Send OTP
              </button>
            </form>
          ) : (
            <Box className="verify-otp-form">
              <Box className="otp-inputs-container">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-input-${index}`}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleOtpChange(index, e.target.value)}
                    onKeyDown={e => handleKeyDown(index, e)}
                    className="otp-input"
                  />
                ))}
              </Box>

              <button
                type="button"
                className="primary-button"
                onClick={handleVerifyOtp}
                disabled={isLoading}
              >
                {isLoading ? 'Verifying...' : 'Sign in'}
              </button>

              <Box className="resend-section">
                <Typography variant="body2" className="resend-text">
                  Didn't receive the code?{' '}
                  <span className="resend-link" onClick={handleResendOtp}>
                    Resend
                  </span>
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default VerifyOTP;
